import classNames from 'classnames';
import { connect as formikConnect } from 'formik';
import map from 'lodash/map';
import noop from 'lodash/noop';
import { compose, mapProps } from 'recompact';

import { FormError } from '../FormError';

import defaultStyles from './styles.module.scss';

type ToggleOption = {
  label: string;
  value: string;
  tabIndex: number;
};
interface ToggleInputUnwiredProps {
  className: string;
  name: string;
  handleClick: (e: MouseEvent, val: string) => void;
  trackClick: (value: string) => void;
  options: ToggleOption[];
  toggleValue: string;
  compactStyle: boolean;
  label?: string;
  error?: string;
  altStyles?: object;
  onClickCallback?: (value: string) => void;
  noMarginBottom: boolean;
  isOnboardingFlow?: boolean;
}

const ToggleInputUnwired = ({
  name,
  label,
  className,
  handleClick,
  trackClick,
  options,
  toggleValue,
  compactStyle,
  error,
  altStyles,
  onClickCallback,
  noMarginBottom,
  isOnboardingFlow,
}: ToggleInputUnwiredProps) => {
  const errorId = `error-${name}`;

  const handleKeyDown = event => {
    if (event?.key === 'Enter') {
      event?.target?.click();
    }
  };

  const styles = altStyles ? altStyles : defaultStyles;
  const isLabelQuestion = label?.includes('?');

  return (
    <div
      className={classNames(
        styles.component,
        className,
        { [styles.compact]: compactStyle },
        { [styles.isLabelQuestion]: isLabelQuestion },
        { [styles.noMarginBottom]: noMarginBottom }
      )}
    >
      <span
        className={classNames(styles.label, { [styles.isLabelQuestion]: isLabelQuestion, [styles.error]: !!error })}
      >
        {label}
      </span>
      <div role="radiogroup" aria-label="Toggle Radio Grouping" className={styles.buttonContainer}>
        {map(options, ({ label, value }, index) => {
          const isChecked = value === toggleValue;

          const buttonId = index === 0 ? name : `${name}.${index}`;
          return (
            <label htmlFor={buttonId} className={styles.labelWrapper} key={`toggleInput-${name}-${value}-label`}>
              <div
                id={buttonId}
                aria-checked={isChecked}
                className={classNames(styles.button, {
                  [styles.selected]: value === toggleValue,
                  [styles.error]: error,
                  [styles.isOnboardingFlow]: isOnboardingFlow,
                  [styles.marginLef]: index === options.length - 1,
                })}
                key={`toggleInput-${name}-${value}-btn`}
                onClick={e => {
                  handleClick(e, value);
                  trackClick(value);
                  if (onClickCallback) {
                    onClickCallback(value);
                  }
                }}
                onKeyDown={handleKeyDown}
                value={value}
                // A div tag is not part of the tabbing order unless you specify
                // 0 which inserts it naturally (the order of the html elements)
                tabIndex={0}
                role="radio"
                aria-describedby={errorId}
                data-cy={`toggle_${index}`}
              >
                {label}
              </div>
            </label>
          );
        })}
      </div>
      <FormError errorId={errorId} error={error} className={styles.spanError} />
    </div>
  );
};

ToggleInputUnwired.defaultProps = {
  handleClick: noop,
  trackClick: noop,
  compactStyle: false,
};

const ToggleInputFormikWired = compose(
  formikConnect,
  mapProps(parentProps => {
    const { name, formik } = parentProps;
    const { values, setFieldValue, touched, errors } = formik;
    const error = touched[name] && errors[name];

    const handleClick = (event, value) => {
      event.preventDefault();
      event.stopPropagation();
      setFieldValue(name, value);
    };

    return {
      ...parentProps,
      handleClick,
      toggleValue: values[name],
      error,
    };
  })
)(ToggleInputUnwired);

export default ToggleInputFormikWired;
